import type { FC } from 'react';
import React from 'react';
import { css } from '@compiled/react';

import {
	Attribution,
	ErrorBoundary,
	ErrorDisplay,
	withErrorBoundary,
} from '@confluence/error-boundary';
import {
	BACKGROUND_SCRIPT_APPS_FORGE_EXPERIENCE,
	ExperienceStart,
} from '@confluence/experience-tracker';
import { isErrorMarkedAsHandled } from '@confluence/graphql';
import {
	BackgroundScriptForgeApp,
	PageBannerForgeApp,
	FORGE_MODULE_BACKGROUND_SCRIPT,
	FORGE_MODULE_PAGE_BANNER,
	LazyForgeUIExtensions,
	getAppId,
	type ForgeUIExtensionType,
} from '@confluence/forge-ui';

// This is exported solely for testing purposes

const invisible = css({ display: 'none' });
export const RenderForgeAppsAsBackgroundScripts = (
	forgeModules: ReadonlyArray<ForgeUIExtensionType>,
	loading: boolean,
	error?: Error,
) => {
	if (loading) return null;

	if (error) {
		if (!isErrorMarkedAsHandled(error)) {
			return <ErrorDisplay error={error} />;
		}
		return null;
	}

	return forgeModules.map(
		withErrorBoundary({
			attribution: Attribution.ECOSYSTEM,
		})((app: ForgeUIExtensionType) => (
			<ErrorBoundary
				key={getAppId(app)}
				attributes={{ appId: getAppId(app) }}
				attribution={Attribution.ECOSYSTEM}
			>
				{/* Render the background script Forge apps inside an invisible div - we don't want to show any UI for these apps */}
				<div css={invisible}>
					<ExperienceStart name={BACKGROUND_SCRIPT_APPS_FORGE_EXPERIENCE} id={getAppId(app)} />
					<BackgroundScriptForgeApp
						app={app}
						// The background script module is rendered at the MainLayout level. Certain page navigations like navigating between pages within the same space will not trigger a re-invocation of the Forge app.
						// Adding the pageRoute to the extensionData will allow the Forge app to re-render when the route changes
						extensionData={{
							type: FORGE_MODULE_BACKGROUND_SCRIPT,
						}}
					/>
				</div>
			</ErrorBoundary>
		)),
	);
};

export const RenderBackgroundScriptApps: FC<{}> = () => (
	<LazyForgeUIExtensions
		moduleType={FORGE_MODULE_BACKGROUND_SCRIPT}
		render={RenderForgeAppsAsBackgroundScripts}
	/>
);

// This is exported solely for testing purposes
export const RenderForgeAppsAsPageBanners = (
	forgeModules: ReadonlyArray<ForgeUIExtensionType>,
	loading: boolean,
	error?: Error,
) => {
	if (loading) return null;

	if (error) {
		if (!isErrorMarkedAsHandled(error)) {
			return <ErrorDisplay error={error} />;
		}
		return null;
	}

	return forgeModules.map(
		withErrorBoundary({
			attribution: Attribution.ECOSYSTEM,
		})((app: ForgeUIExtensionType) => (
			<ErrorBoundary
				key={getAppId(app)}
				attributes={{ appId: getAppId(app) }}
				attribution={Attribution.ECOSYSTEM}
			>
				<PageBannerForgeApp
					app={app}
					extensionData={{
						type: FORGE_MODULE_PAGE_BANNER,
					}}
				/>
			</ErrorBoundary>
		)),
	);
};

export const RenderPageBannerForgeApps: FC<{}> = () => {
	return (
		<LazyForgeUIExtensions
			moduleType={FORGE_MODULE_PAGE_BANNER}
			render={RenderForgeAppsAsPageBanners}
		/>
	);
};
